import { useEffect } from 'react';
import { BacksolveSourceRow } from 'api';
import { useStore } from 'common/store';
import { CurrentCompanyMeasurementDate } from 'common/types/measurementDate';
import { UseStoreValues } from 'common/types/store';
import { getAsOfRecord } from 'pages/Portfolio/pages/CompanySummary/components/CompanyOverview/ZXIndexValueCard/ZXIndexValueCard.utils';
import { SOURCE_ROW_ZX_INDEX_RECORD_ID } from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/sheetAliases';
import { useGetCompanyZanbatoClosedTransactions, useGetCompanyZanbatoPricing } from 'services/hooks/companies';

type UseZxIndexSourceRowParams = {
  backsolveType: string | undefined;
  measurementDate: CurrentCompanyMeasurementDate | null | undefined;
  getSourceRows: () => BacksolveSourceRow[];
  resetSourceRowsSheetWithRows: (rows: BacksolveSourceRow[]) => void;
};

const getCutoffDateStr = (measurementDateStr: string): string => {
  const d = new Date(measurementDateStr);
  d.setUTCFullYear(d.getUTCFullYear() - 1);
  return d.toISOString().slice(0, 10);
};

const isClosedTransactionSourceRow = (row: BacksolveSourceRow): boolean =>
  row.source_record_id != null && row.source_record_id !== SOURCE_ROW_ZX_INDEX_RECORD_ID;

const useZxIndexSourceRow = ({
  backsolveType,
  measurementDate,
  getSourceRows,
  resetSourceRowsSheetWithRows,
}: UseZxIndexSourceRowParams): void => {
  const [storeValue] = useStore() as unknown as UseStoreValues;
  const companyId: number | undefined = storeValue?.companyInfo?.id;
  const isStApproach = backsolveType === 'secondary_transactions';

  const { data: zxPricingData } = useGetCompanyZanbatoPricing(isStApproach ? companyId : undefined);
  const { data: closedTransactionsData } = useGetCompanyZanbatoClosedTransactions(
    isStApproach ? companyId : undefined
  );

  // ZX Index Price is prepended so it always appears before closed transactions
  useEffect(() => {
    if (!isStApproach) {
      return;
    }
    if (!zxPricingData?.pricing?.length) {
      return;
    }
    const currentRows = getSourceRows();
    const zxRowIndex = currentRows.findIndex(r => r.source_record_id === SOURCE_ROW_ZX_INDEX_RECORD_ID);
    if (zxRowIndex > 0) {
      const zxRow = currentRows[zxRowIndex];
      resetSourceRowsSheetWithRows([zxRow, ...currentRows.filter((_, index) => index !== zxRowIndex)]);
      return;
    }
    if (zxRowIndex === 0) {
      return;
    }
    const asOfRecord = getAsOfRecord(zxPricingData.pricing, measurementDate?.date);
    if (asOfRecord?.zx_index_value == null) {
      return;
    }
    const zxRow: BacksolveSourceRow = {
      title: 'ZX Index Price',
      date: measurementDate?.date ?? null,
      source: 'zanbato.com',
      size: null,
      price_per_share: asOfRecord.zx_index_value,
      weighting: '0',
      source_record_id: SOURCE_ROW_ZX_INDEX_RECORD_ID,
      order: 1,
    };
    resetSourceRowsSheetWithRows([zxRow, ...currentRows]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStApproach, zxPricingData, measurementDate?.date]);

  // Closed transactions are appended so they appear after the ZX Index Price row
  useEffect(() => {
    if (!isStApproach) {
      return;
    }
    const transactions = closedTransactionsData?.transactions ?? [];
    if (!transactions.length) {
      return;
    }

    const currentRows = getSourceRows();
    const hasCtRows = currentRows.some(isClosedTransactionSourceRow);
    if (hasCtRows) {
      return;
    }

    const measurementDateStr = measurementDate?.date;
    const cutoffStr = measurementDateStr ? getCutoffDateStr(measurementDateStr) : null;

    const ctRows: BacksolveSourceRow[] = transactions
      .filter(t => {
        if (!t.closing_date) {
          return false;
        }
        if (cutoffStr && t.closing_date < cutoffStr) {
          return false;
        }
        if (measurementDateStr && t.closing_date > measurementDateStr) {
          return false;
        }
        return true;
      })
      .sort((a, b) => (b.closing_date ?? '').localeCompare(a.closing_date ?? ''))
      .map((t, idx) => ({
        title: t.security_type ? `Closed Transaction (${t.security_type})` : 'Closed Transaction',
        date: t.closing_date ?? null,
        source: 'zanbato.com',
        size: t.total_value ?? null,
        price_per_share: t.price_per_share ?? '0',
        weighting: '0',
        source_record_id: t.id != null ? String(t.id) : String(idx),
        order: idx + 2,
      }));

    if (ctRows.length === 0) {
      return;
    }
    resetSourceRowsSheetWithRows([...currentRows, ...ctRows]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStApproach, closedTransactionsData, measurementDate?.date]);
};

export default useZxIndexSourceRow;
